import React, { useEffect, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../../lib/api';
import OnboardingPage from '../../../pages/OnboardingPage';
import { 
  storeOnboardingToken, 
  shouldTrustCachedToken, 
  clearOnboardingCache,
  type OnboardingStatus 
} from '../../../lib/onboardingCache';

interface OrganizationGuardProps {
  children: React.ReactNode;
}

type OrgGuardState = 
  | { type: 'LOADING' }
  | { type: 'ASSIGNED' }           // User has organization → Allow access
  | { type: 'PENDING' }            // Join request waiting → Show pending page  
  | { type: 'UNASSIGNED' }         // No organization → Show onboarding modal
  | { type: 'ERROR', error: string }; // API failed → Show error/retry

/**
 * OrganizationGuard - Single Responsibility: Check organization assignment status
 * 
 * Prerequisites: User must be authenticated (handled by AuthGuard)
 * 
 * States:
 * - LOADING: Checking organization status
 * - ASSIGNED: User has organization access → Render children
 * - PENDING: User has pending join request → Show pending page
 * - UNASSIGNED: User needs to complete onboarding → Show onboarding modal
 * - ERROR: API failure → Show error with retry option
 */
export default function OrganizationGuard({ children }: OrganizationGuardProps) {
  const { user } = useUser(); // We know user exists because AuthGuard passed
  const [state, setState] = useState<OrgGuardState>({ type: 'LOADING' });
  const [isChecking, setIsChecking] = useState(false);

  // Reusable function to check organization status
  const checkOrganizationStatus = useCallback(async () => {
    if (!user || isChecking) {
      return;
    }
    
    // Ensure Clerk is fully initialized before making API calls
    if (!(window as any).Clerk?.session) {
      console.warn('🔑 OrganizationGuard: Clerk session not ready, delaying check');
      setTimeout(() => checkOrganizationStatus(), 1000);
      return;
    }
    
    setIsChecking(true);

    try {
      // Check cache first for fast response
      const cacheValidation = shouldTrustCachedToken(user.id);
      
      if (cacheValidation.canTrust && cacheValidation.status) {
        setState(mapOnboardingStatusToState(cacheValidation.status));
        setIsChecking(false);
        return;
      }

      // No valid cache, make API call
      // The backend API will handle impersonation context automatically
      const response = await api.get('/onboarding/status');
      
      
      const userData = response.data.data || response.data;
      const onboardingToken = response.data.onboardingToken;
      
      // Store token if provided
      if (onboardingToken) {
        storeOnboardingToken(onboardingToken);
      }
      
      // Determine status using same logic as original OnboardingGuard
      const organizationId = userData?.organizationId || userData?.profile?.organizationId;
      const hasPendingRequest = userData?.hasPendingJoinRequest;
      const knownAdminEmails = ['bertram.b.fields@gmail.com', 'daniel@nexuscrm.com.au', 'daniel@nexusaspyre.com'];
      const isKnownAdmin = knownAdminEmails.includes(userData?.email?.toLowerCase());
      
      let onboardingStatus: OnboardingStatus;
      if ((userData && organizationId && organizationId !== null) || isKnownAdmin) {
        onboardingStatus = true;
      } else if (hasPendingRequest) {
        onboardingStatus = 'pending';
      } else {
        onboardingStatus = false;
      }
      
      
      
      setState(mapOnboardingStatusToState(onboardingStatus));
    } catch (error: any) {
      clearOnboardingCache();
      setState({ 
        type: 'ERROR', 
        error: error.message || 'Failed to connect to server' 
      });
    } finally {
      setIsChecking(false);
    }
  }, [user?.id]); // Only depend on user ID, not isChecking to prevent infinite loop

  // Debug logging (only on state changes, not every render)
  useEffect(() => {
  }, [state.type, user?.id, isChecking]); // Only log when state actually changes

  // Run organization check when component mounts or user changes
  useEffect(() => {
    if (!user?.id || isChecking) return;
    
    // Always run the organization check - the backend API will handle impersonation context
    // The /onboarding/status endpoint already handles impersonation correctly
    if (state.type === 'LOADING') {
      checkOrganizationStatus();
    }
  }, [user?.id, state.type, isChecking]); // Re-run when state changes

  // Helper function to map OnboardingStatus to our state
  function mapOnboardingStatusToState(status: OnboardingStatus): OrgGuardState {
    switch (status) {
      case true:
        return { type: 'ASSIGNED' };
      case 'pending':
        return { type: 'PENDING' };
      case false:
        return { type: 'UNASSIGNED' };
    }
  }

  // Handle different states
  switch (state.type) {
    case 'LOADING':
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg text-gray-600">Checking your organization access...</div>
        </div>
      );

    case 'ASSIGNED':
      // Debug flag for guard logging (set to true only when debugging auth issues)
      const DEBUG_GUARDS = false;
      if (DEBUG_GUARDS && import.meta.env.DEV) {
      }
      return <>{children}</>;

    case 'PENDING':
      return <OnboardingPage showPendingRequest={true} />;

    case 'UNASSIGNED':
      // Handle special case for known admin users
      if (user?.emailAddresses?.[0]?.emailAddress === 'daniel@nexusaspyre.com') {
        window.location.href = '/admin';
        return null;
      }
      return <OnboardingPage />;

    case 'ERROR':
      return (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-lg text-red-600 mb-4">Unable to verify organization access</div>
          <div className="text-sm text-gray-600 mb-4">Error: {state.error}</div>
          <button 
            onClick={() => {
              setState({ type: 'LOADING' });
              checkOrganizationStatus();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      );

    default:
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg text-red-600">Unknown organization guard state</div>
        </div>
      );
  }
}
